import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';
import type { FinancePeriod } from './use-finance-summary.js';

export interface ExpenseCategory {
  category: string;
  amount: number;
  status: string;
  count: number;
}

export interface ExpenseStatusSummary {
  verified: number;
  caution: number;
  flagged: number;
  pending: number;
}

export interface ExpenseDetailRow {
  id: string;
  week_ending?: string;
  category: string;
  amount: number;
  source?: string;
  period_basis?: string;
  status: string;
  notes?: string;
}

export interface ExpenseWeeklyTrend {
  week_ending: string;
  total: number;
}

export interface ExpensesReportData {
  period: FinancePeriod;
  treemap: ExpenseCategory[];
  status_summary: ExpenseStatusSummary;
  detail: ExpenseDetailRow[];
  weekly_trend: ExpenseWeeklyTrend[];
}

interface ExpensesResponse extends ExpensesReportData {
  error?: string;
}

export function useFinanceExpenses(periodType: string) {
  const [data, setData] = useState<ExpensesReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (periodType) params.set('period', periodType);

      const resp = await apiFetch<ExpensesResponse>(`/api/finance/expenses?${params.toString()}`);
      if (resp?.error) throw new Error(resp.error);

      setData(resp);
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : 'Expenses report load failed');
    } finally {
      setLoading(false);
    }
  }, [periodType]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, refreshExpenses: load };
}
